import React from 'react';
import { useSearchParams } from 'react-router-dom';
import Fees from './Fees';
import LateLien from './LateLien';

/**
 * Combined Fees settings page. Consolidates the old standalone
 * /settings/fees (fee catalog) and /settings/late-lien (automatic late-fee
 * tiers + grace) into ONE page with two query-param-routed internal tabs,
 * matching the UnitsPage tab pattern so deep-links + the tab strip work:
 *
 *   /settings/fees                  -> Fee Catalog (default)
 *   /settings/fees?tab=late-lien    -> Late Fees
 *
 * /settings/late-lien redirects to /settings/fees?tab=late-lien via the
 * App.js route table for back-compat. Both child pages read facilityId from
 * the FacilityContext (useFacility), so no props are threaded here — they
 * render chrome-free embedded under the tab.
 */
const TABS = [
  { key: 'catalog', label: 'Fee Catalog', desc: 'Operator-defined fee catalog charged manually or via automation' },
  // Label says LATE FEES only. Nothing on this screen starts a lien —
  // no code path sets a rental to Lien; that is the lien workflow's job,
  // where the statutory notices and deadlines are tracked. Naming it
  // "Late / Lien" implied this screen could begin a legal process.
  // The route key stays `late-lien` so existing links keep working.
  { key: 'late-lien', label: 'Late Fees', desc: 'Automatic late-fee tiers' },
];

const FeesSettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Default tab is 'catalog' (matches the first tab in the strip).
  const rawTab = searchParams.get('tab');
  const activeTab = TABS.find((t) => t.key === rawTab) ? rawTab : 'catalog';

  const setTab = (key) => {
    const params = new URLSearchParams(searchParams);
    if (key === 'catalog') params.delete('tab');
    else params.set('tab', key);
    setSearchParams(params, { replace: true });
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-slate-900">Fees</h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          {TABS.find((t) => t.key === activeTab)?.desc}
        </p>
      </div>

      {/* Tab strip */}
      <div className="border-b border-slate-200 mb-5">
        <nav className="-mb-px flex gap-6" aria-label="Fees tabs">
          {TABS.map((t) => {
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`whitespace-nowrap py-2.5 px-1 border-b-2 text-[13px] font-medium transition-colors ${
                  active
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Active tab content. Each child reads facilityId from context. */}
      {activeTab === 'catalog' && <Fees />}
      {activeTab === 'late-lien' && <LateLien />}
    </div>
  );
};

export default FeesSettings;
